import diffusion from 'diffusion';
import parseUrl from '../parseUrl';

const NOTIFICATION_TYPES = {
  cashIn: 'CASH_IN',
  isInPlay: 'IS_IN_PLAY'
};

export const CRITICAL_FIELDS = {
  [NOTIFICATION_TYPES.cashIn]: {
    event: ['isInPlay', 'offtime', 'settled', 'status'],
    market: ['active', 'settled'],
    selection: ['currentPriceNum', 'currentPriceDen', 'settled']
  },
  [NOTIFICATION_TYPES.isInPlay]: {
    event: ['isInPlay', 'offtime']
  }
};

const FIELDS = {
  market: ['order', 'displayed', 'name', 'status', 'hcapValue', 'marketGroupId',
    'blurb', 'sort', 'eachWay', 'eachWayPlaces', 'eachWayFactorNum', 'eachWayFactorDen',
    'marketGroupCollectionId', 'hasInPlay', 'active', 'settled'],
  selection: ['order', 'displayed', 'status', 'currentPriceNum', 'currentPriceDen',
    'cashoutPriceNum', 'cashoutPriceDen', 'name', 'csHome', 'csAway', 'fbResult', 'settled',
    'result', 'resultConf', 'place', 'priceHistory'],
  event: ['order', 'displayed', 'isInPlay', 'status', 'settled', 'startDateTime', 'active', 'offtime']
};

const TYPE_TAGS = {
  FUNCTION: 'function',
  UNDEFINED: 'undefined'
};

const TYPES_PREFIXES = {
  event: 'EV',
  market: 'MA',
  selection: 'OU'
};

const TOPIC_LEVELS = {
  event: 'event',
  market: 'market',
  selection: 'selection'
};

const TOPIC_PREFIX = 'PDS/';
const SUBSCRIBE_TIMEOUT = 3000;

class CashInPriceNotifierV6 {
  constructor() {
    this.session = null;
    this.didConnect = false;
    this.connecting = false;
    this.topics = [];
    this.subscribedTopics = {};
    this.watched = {};
    this.currentState = {};
    this.onDataReceived = this.onDataReceived.bind(this);
  }

  static createTopic({ event, market, selection }) {
    let topic = '';

    if (!event) {
      return topic;
    }

    topic = `${TOPIC_PREFIX}${event}`;

    if (!market) {
      return topic;
    }

    topic = `${topic}/${market}`;

    if (!selection) {
      return topic;
    }

    topic = `${topic}/${selection}`;

    return topic;
  }

  static getType(topic) {
    const topicLevelParts = topic.split('/');
    const deepestLevel = topicLevelParts[topicLevelParts.length - 1];
    const deepestLevelParts = deepestLevel.split('_');
    const topicLevelPrefix = deepestLevelParts[deepestLevelParts.length - 1].slice(0, 2);

    switch (topicLevelPrefix) {
      case TYPES_PREFIXES.selection: {
        return TOPIC_LEVELS.selection;
      }
      case TYPES_PREFIXES.market: {
        return TOPIC_LEVELS.market;
      }
      default: {
        return TOPIC_LEVELS.event;
      }
    }
  }

  static getEventId(topic) {
    const topicLevelParts = topic.split('/');

    return topicLevelParts[1];
  }

  static pickCriticalFields(notificationType, fields, type) {
    const criticalFields = CRITICAL_FIELDS[notificationType];

    return fields.reduce((acc, next, index) => {
      const aggregatedFields = acc;
      const key = FIELDS[type][index];

      if (key && criticalFields[type].includes(key)) {
        aggregatedFields[key] = next;
      }

      return aggregatedFields;
    }, {});
  }

  static mergeFieldsWithState(fields, type, currentState) {
    return Object.assign(currentState, fields.reduce((acc, next, index) => {
      const aggregatedFields = acc;
      const key = FIELDS[type][index];
      if (key) {
        aggregatedFields[key] = next || currentState[key];
      }
      return aggregatedFields;
    }, {}));
  }

  on(eventType, handler) {
    if (typeof handler !== TYPE_TAGS.FUNCTION && typeof handler !== TYPE_TAGS.UNDEFINED) {
      // eslint-disable-next-line no-console
      console.error('Handler is not a function / undefined');
    }

    switch (eventType) {
      case 'connect': {
        this.onConnect = handler;
        break;
      }
      case 'disconnect': {
        this.onDisconnect = handler;
        break;
      }
      case 'cashInPriceChange': {
        this.onCashInPriceChange = handler;
        break;
      }
      case 'eventInPlayChange': {
        this.onEventInPlay = handler;
        break;
      }
      case 'timeout': {
        this.onTimeout = handler;
        break;
      }
      case 'error': {
        this.onError = handler;
        break;
      }
      default: {
        // eslint-disable-next-line no-console
        console.error('Event type unrecognized');
      }
    }
  }

  off(eventType) {
    this.on(eventType, undefined);
  }

  connect(params) {
    if (this.connecting || this.didConnect) {
      return;
    }

    const url = parseUrl(params.wsURL);
    const connectParams = {
      host: url.host,
      port: url.port,
      path: url.path,
      secure: url.secure,
      properties: { src: 'retail-plus' }
    };

    this.connecting = true;
    diffusion.connect(connectParams)
      .then((session) => {
        this.session = session;
        this.didConnect = true;
        this.connecting = false;

        this.session.on({
          disconnect: () => {
            this.didConnect = false;
            if (this.onDisconnect) {
              this.onDisconnect();
            }
          },
          error: (error) => {
            this.didConnect = false;
            if (this.onError) {
              this.onError(error);
            }
          },
          close: () => {
            this.didConnect = false;
            this.resetSubscriptions();
            if (this.onDisconnect) {
              this.onDisconnect();
            }
          }
        });

        if (this.onConnect) {
          this.onConnect();
        }
        this.forwardSubscriptions();
      })
      .catch((error) => {
        this.connecting = false;
        this.didConnect = false;
        if (this.onError) {
          this.onError(error);
        }
      });
  }

  disconnect() {
    this.didConnect = false;
    this.connecting = false;
    this.resetSubscriptions();
    if (this.session && !this.session.isClosed()) {
      this.session.close();
    }
    this.session = null;
    if (typeof this.onDisconnect === TYPE_TAGS.FUNCTION) {
      this.onDisconnect();
    }
  }

  watchAdd(topic) {
    if (this.watched[topic]) {
      clearTimeout(this.watched[topic]);
    }
    this.watched[topic] = setTimeout(() => {
      if (this.onTimeout) {
        const [, event, market, selection] = topic.split('/');
        this.onTimeout({ event, market, selection });
      }
    }, SUBSCRIBE_TIMEOUT);
  }

  watchRemove(topic) {
    clearTimeout(this.watched[topic]);
    this.watched[topic] = null;
  }

  add(params) {
    const topic = CashInPriceNotifierV6.createTopic(params);
    this.watchAdd(topic);

    if (topic) {
      if (!this.topics.includes(topic)) {
        this.topics.push(topic);
      }
      if (this.subscribedTopics[topic] !== true) {
        this.subscribedTopics[topic] = false;
      }
      this.currentState[topic] = this.currentState[topic] || {};
      this.forwardSubscriptions();
    }
  }

  remove(params) {
    const topic = CashInPriceNotifierV6.createTopic(params);
    if (!topic) {
      return false;
    }

    const topicIndex = this.topics.indexOf(topic);

    if (topicIndex >= 0) {
      this.topics.splice(topicIndex, 1);
      this.currentState[topic] = null;
      this.subscribedTopics[topic] = null;
      if (this.session && this.didConnect) {
        this.session.unsubscribe(topic);
      }
    }

    return true;
  }

  removeAll() {
    this.currentState = {};
    this.subscribedTopics = {};
    for (let i = 0, { length } = this.topics; i <= length; i++) {
      const topic = this.topics.pop();
      if (topic && this.session && this.didConnect) {
        this.session.unsubscribe(topic);
      }
    }
  }

  forwardSubscriptions() {
    if (this.session && this.didConnect) {
      Object.keys(this.subscribedTopics)
        .filter(topic => this.subscribedTopics[topic] === false)
        .forEach(topic => {
          this.session.addStream(topic, diffusion.datatypes.recordv2())
            .on('value', this.onDataReceived);
          this.session.select(topic).then(() => {
            this.subscribedTopics[topic] = true;
          });
        });
    }
  }

  resetSubscriptions() {
    Object.keys(this.subscribedTopics)
      .filter(topic => this.subscribedTopics[topic] === true)
      .forEach(topic => {
        this.subscribedTopics[topic] = false;
      });
  }

  onDataReceived(topic, specification, data) {
    try {
      const type = CashInPriceNotifierV6.getType(topic);
      const eventId = CashInPriceNotifierV6.getEventId(topic);
      const recordValue = (data && typeof data.get === 'function') ? data.get() : data;
      const records = (recordValue && typeof recordValue.asRecords === 'function')
        ? recordValue.asRecords()
        : [];
      const fields = records.length > 0
        ? records[0]
        : ((recordValue && typeof recordValue.asFields === 'function') ? recordValue.asFields() : []);

      if (!Array.isArray(fields)) {
        return;
      }

      this.watchRemove(topic);
      this.currentState[topic] = CashInPriceNotifierV6.mergeFieldsWithState(
        fields,
        type,
        this.currentState[topic] || {}
      );

      if (this.onCashInPriceChange) {
        const cashInCriticalData = CashInPriceNotifierV6.pickCriticalFields(
          NOTIFICATION_TYPES.cashIn,
          fields,
          type
        );
        const cashInCriticalValues = Object.keys(cashInCriticalData)
          .map(key => cashInCriticalData[key]);

        if (cashInCriticalValues.some(field => !!field)) {
          this.onCashInPriceChange();
        }
      }

      if (this.onEventInPlay) {
        const isInPlayCriticalData = CashInPriceNotifierV6.pickCriticalFields(
          NOTIFICATION_TYPES.isInPlay,
          fields,
          type
        );
        const isInPlayCriticalValues = Object.values(isInPlayCriticalData);

        if (isInPlayCriticalValues.some(field => !!field)) {
          const topicState = this.currentState[topic] || {};
          const isInPlay = topicState.isInPlay === 'true' || topicState.isInPlay === true;
          const offtime = topicState.offtime || '';
          const offTimeExist = offtime.length > 0;
          const isPreMatch = !isInPlay && !offTimeExist;
          const isPostMatch = !isInPlay && offTimeExist;
          this.onEventInPlay({
            eventId,
            isPreMatch,
            isInPlay,
            isPostMatch
          });
        }
      }
    } catch (error) {
      if (this.onError) {
        this.onError(error);
      }
    }
  }
}

export default CashInPriceNotifierV6;
